/**
 * Pay Period Form Modal
 *
 * Modal for creating or editing pay periods.
 */

import { useState, FormEvent, useEffect } from 'react';
import FormModal from '../../../components/ui/FormModal';
import { createPayPeriod, updatePayPeriod, CreatePayPeriodInput } from '../../../api/payPeriods';
import { getErrorMessage } from '../../../api/client';
import { localDayStartIso, localDayEndIso, toDateInputValue } from '../../../utils/dateFormatting';
import type { PayPeriod } from '../../../types/models';

interface PayPeriodFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  payPeriod?: PayPeriod | null;
}

export default function PayPeriodFormModal({
  isOpen,
  onClose,
  onSuccess,
  payPeriod,
}: PayPeriodFormModalProps) {
  const isEditing = !!payPeriod;

  // Form state
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (payPeriod) {
        setName(payPeriod.name);
        setStartDate(toDateInputValue(payPeriod.startDate));
        setEndDate(toDateInputValue(payPeriod.endDate));
      } else {
        setName('');
        setStartDate('');
        setEndDate('');
      }
      setError(null);
    }
  }, [isOpen, payPeriod]);

  // Generate name based on dates
  const generateName = () => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      // Picker values parse as UTC midnight; read them back in UTC, or every
      // zone west of UTC names the period a day early ("Sep 15-Sep 29").
      const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
      const yearFormatter = new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone: 'UTC' });
      return `${formatter.format(start)}-${formatter.format(end)}, ${yearFormatter.format(end)}`;
    }
    return '';
  };

  // Auto-generate name when dates change (if name is empty or was auto-generated)
  useEffect(() => {
    if (!isEditing && startDate && endDate) {
      const generatedName = generateName();
      if (!name || name === generateName()) {
        setName(generatedName);
      }
    }
  }, [startDate, endDate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const data: CreatePayPeriodInput = {
        name,
        startDate: localDayStartIso(startDate), // Local midnight
        endDate: localDayEndIso(endDate), // Local end of day
      };

      if (isEditing && payPeriod) {
        await updatePayPeriod(payPeriod.id, data);
      } else {
        await createPayPeriod(data);
      }

      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={isEditing ? 'Edit Pay Period' : 'Create Pay Period'}
      submitLabel={isEditing ? 'Save Changes' : 'Create Pay Period'}
      isSubmitting={isSubmitting}
      error={error}
    >
      {/* Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Name
        </label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="e.g., Jan 1-15, 2026"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-rack focus:border-rack"
        />
      </div>

      {/* Date Range */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-rack focus:border-rack"
          />
        </div>
        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
            End Date
          </label>
          <input
            type="date"
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
            min={startDate}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-rack focus:border-rack"
          />
        </div>
      </div>
    </FormModal>
  );
}
